import { FunctionComponent, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppContext from 'src/AppContext';
import { useAcceptInvitation } from 'src/graphql/useAcceptInvitation';
import { invitationPaths, ROUTES } from 'src/routes';
import WrappedSpinner from 'src/shared/WrappedSpinner';
import { getJoinOrganizationEvent } from 'src/tracking';

interface AcceptInvitationPageProps {
    onUserUpdateAfterJoiningOrganization: () => void;
}

const AcceptInvitationPage: FunctionComponent<AcceptInvitationPageProps> = props => {
    const { t } = useTranslation();
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const { userId } = useParams();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const organizationId = searchParams.get('id');

    const acceptInvitation = useAcceptInvitation();
    const fireAcceptInvitation = async (): Promise<void> => {
        acceptInvitation.mutate({
            id: userId!,
            orgId: organizationId!,
        }, {
            onSuccess: async () => {
                context.trackEvent(getJoinOrganizationEvent());
                // Refresh user token and user attribute in global context variable because membership of the user to organization did change.
                // The organization name and other attributes are added to the token after the user joins an organization.
                props.onUserUpdateAfterJoiningOrganization();
                navigate(ROUTES.profile);
            },
            onError: (response: any) => {
                const errorType = response.errors[0].errorType;

                if (errorType === 'InvitationExpired') {
                    navigate(invitationPaths.acceptedExpired(userId!));
                    return;
                }

                if (errorType === 'UserNotFound') {
                    navigate(invitationPaths.acceptedUserNotInOrg(userId!));
                    return;
                }

                if (errorType === 'InvitationConstraintViolated') {
                    navigate(invitationPaths.acceptedWrongEmail(userId!), {
                        state: {
                            // Here, the user already clicked the Join button and the &isAccepted=true was already added to the URL.
                            redirectTo: `${location.pathname}${location.search}`,
                        },
                    });
                    return;
                }

                context.onError(new Error());
            },
        });
    };


    useEffect(() => {

        void fireAcceptInvitation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (<WrappedSpinner label={t('Common.Spinner', 'Loading...')} labelPosition="bottom" />);
};

export default AcceptInvitationPage;
